// Types shared by the main thread (client.ts, manager.ts) and the worker (host.ts, worker.ts).
// Declarations only, so the worker's own tsconfig (no DOM) can import them without dragging in
// the app.
import type { CatalogueMeta, CatalogueQueries, QueryName } from "./types";

/** Where the SQLite file lives: OPFS through sqlite-wasm's SAH pool VFS, else a Blob in IndexedDB. */
export type StoreKind = "opfs-sahpool" | "idb";

/** One file to fetch, with everything the worker needs to fetch, check and store it. */
export interface InstallPlan {
  version: string;
  builtAt: string;
  /** Absolute URL of the file to download: the bucket's custom domain when the manifest names one. */
  url: string;
  /** The edge's URL of the same file, tried when `url` fails; absent when `url` already is the edge. */
  fallbackUrl?: string;
  /** Lower-case hex SHA-256 of the downloaded bytes (the .br itself when compressed). */
  sha256: string;
  /** Size of the download, for progress and as a length check. */
  bytes: number;
  encoding: "br" | "identity";
  /** Size of the SQLite file after decoding, when the manifest lists it. */
  decodedBytes?: number;
}

/** One delta of a chain: where to fetch it and what it must be, from the manifest. */
export interface DeltaStep {
  from: string;
  to: string;
  url: string;
  /** The edge's URL of the same delta, tried when `url` fails. */
  fallbackUrl?: string;
  /** Lower-case hex SHA-256 of the downloaded (brotli) bytes. */
  sha256: string;
  /** Download size, for progress and as a length check. */
  bytes: number;
}

/** The increments that take the installed catalogue to `version`, in order. */
export interface DeltaPlan {
  version: string;
  builtAt: string;
  steps: DeltaStep[];
  /** Sum of the steps' download sizes. */
  bytes: number;
  /** Content digest (druif's ContentDigest) the database must have once every step is applied. */
  contentSha256: string;
}

/** A catalogue file the device holds. The manager persists it as the pointer to the live version. */
export interface InstalledRef {
  version: string;
  builtAt: string;
  file: string;
  store: StoreKind;
  /** Size of the SQLite file on the device. */
  bytes: number;
  /** When this device last installed or updated it (ISO 8601); absent on pointers older than deltas. */
  updatedAt?: string | undefined;
  /** Whether that was a full download or an applied delta. */
  updatedBy?: "download" | "delta" | undefined;
}

export interface InstallProgress {
  /** "download" counts compressed bytes received, "install" decoded bytes written to storage. */
  phase: "download" | "install";
  loaded: number;
  total: number;
}

/** A query with its arguments, one member per CatalogueQueries method so a switch narrows `args`. */
export type QueryRequest = {
  [N in QueryName]: { name: N; args: Parameters<CatalogueQueries[N]> };
}[QueryName];

export type WorkerCall =
  | { op: "install"; plan: InstallPlan }
  | { op: "applyDelta"; plan: DeltaPlan }
  | { op: "open"; ref: InstalledRef }
  | { op: "prune"; keep: InstalledRef | null }
  | { op: "query"; query: QueryRequest };

export type WorkerRequest = WorkerCall & { id: number };

export interface SerializedError {
  name: string;
  message: string;
  /** CatalogueFormatError.problem, when that is what failed. */
  problem?: string;
  /** DownloadError.failure, when that is what failed. */
  failure?: string;
}

export type WorkerMessage =
  | { id: number; kind: "result"; result: unknown }
  | { id: number; kind: "error"; error: SerializedError }
  | { id: number; kind: "progress"; progress: InstallProgress };

/** What the main thread can ask of the catalogue host, in-worker or (in tests) in-process. */
export interface CatalogueBackend {
  install(
    plan: InstallPlan,
    onProgress: (progress: InstallProgress) => void,
  ): Promise<InstalledRef>;
  /**
   * Fetches `plan`'s deltas, applies them to a copy of the open catalogue, checks the result's content
   * digest and stores the copy beside the live file, which is never touched. Rejects (and leaves nothing
   * behind) on any failure; the caller then falls back to a full install.
   */
  applyDelta(
    plan: DeltaPlan,
    onProgress: (progress: InstallProgress) => void,
  ): Promise<InstalledRef>;
  open(ref: InstalledRef): Promise<CatalogueMeta>;
  /** Deletes every catalogue file except `keep`'s (all of them when null). */
  prune(keep: InstalledRef | null): Promise<void>;
  query<N extends QueryName>(
    name: N,
    ...args: Parameters<CatalogueQueries[N]>
  ): Promise<ReturnType<CatalogueQueries[N]>>;
}

/**
 * TypeScript cannot correlate a generic query name with its argument tuple, so the two casts that
 * connect CatalogueBackend.query's typed signature to the untyped message live here, once.
 */
export function queryRequest<N extends QueryName>(
  name: N,
  args: Parameters<CatalogueQueries[N]>,
): QueryRequest {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above.
  return { name, args } as QueryRequest;
}

/** The typed result of query `name`, from the untyped value that came back. */
export function queryResult<N extends QueryName>(
  _name: N,
  value: unknown,
): ReturnType<CatalogueQueries[N]> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above.
  return value as ReturnType<CatalogueQueries[N]>;
}
